import React, { useState, useEffect, useCallback } from 'react';
import { Link, Text, Flex, theme, Button, Box } from '@xometry/xometry_loft';
import { Cell } from 'react-table';
import Table from '../../../components/Table';
import { WWW_ORIGIN } from '../../../config';
import { Organization } from '../../../schema/generated/schema';
import OrganizationMemberHelper, {
  PAGINATION_PAGE_SIZE,
} from '../../Organization/TeamSettings/TeamMemberManagement/helper';

const ActiveMemberColumns = () => {
  return [
    {
      Header: 'Team Name',
      accessor: 'name',
      Cell: ({ cell }: { cell: Cell }) => (
        <Text kind="label" padding="0px">
          {cell.value}
        </Text>
      ),
    },
    {
      Header: 'Members',
      accessor: 'activeMembers',
      Cell: ({ cell }: { cell: Cell }) => (
        <Text kind="body-sm">
          {cell.value} Member{cell.value > 1 ? 's' : ''}
        </Text>
      ),
    },
    {
      accessor: 'id',
      Cell: ({ cell }: { cell: Cell }) => (
        <Link
          href={`${WWW_ORIGIN}/account/organization/${cell.value}/settings`}
        >
          View Team Settings
        </Link>
      ),
    },
  ];
};

interface MyTeamTableProps {
  userOrganizations: Partial<Organization[]>;
  loadingUserOrganizations: boolean;
}

const MyTeamTab = ({
  userOrganizations,
  loadingUserOrganizations,
}: MyTeamTableProps) => {
  const [maxPages, setMaxPages] = useState(0);
  const [selectedPage, setSelectedPage] = useState(1);
  const [currentMembers, setCurrentMembers] = useState([]);

  useEffect(() => {
    if (userOrganizations.length > 0) {
      setMaxPages(Math.ceil(userOrganizations.length / PAGINATION_PAGE_SIZE));
      setCurrentMembers(userOrganizations.slice(selectedPage - 1, 5));
    }
  }, [userOrganizations.length]);

  // React callback to manage fetch calls
  const fetchMemberData = useCallback(
    (currentPage = 1, pageSize = 5) => {
      OrganizationMemberHelper.fetchMembersInMemory(
        userOrganizations,
        setCurrentMembers,
        currentPage,
        pageSize
      );
    },
    [selectedPage]
  );

  return (
    <>
      {userOrganizations.length > 0 && (
        <Table
          data={currentMembers}
          columns={ActiveMemberColumns()}
          loading={loadingUserOrganizations}
          hideHeader={false}
          showSelectColumn={false}
          isReadOnly={true}
          recordPageCount={maxPages}
          pageSize={PAGINATION_PAGE_SIZE}
          selectedPage={selectedPage}
          setSelectedPage={setSelectedPage}
          fetchCallback={fetchMemberData}
        />
      )}
      {userOrganizations.length === 0 && (
        <Flex
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          paddingY="80px"
          width="100%"
          margin="auto"
        >
          <Text
            kind="panel-title"
            marginTop={theme.space[5]}
            display="block"
            fontWeight={theme.fontWeights.semibold}
          >
            You Have Not Joined or Created a Team
          </Text>
          <Text
            kind="body-sm"
            marginTop={theme.space[2]}
            display="block"
            maxWidth="540px"
            textAlign="center"
          >
            Teams allow you to share quotes and orders with other users in a
            single dashboard. Create a new team and invite your coworkers, or
            ask your manager to create a team for your organization.
          </Text>
          <Box marginY={theme.space[4]}>
            <Link href={`${WWW_ORIGIN}/account/organization/create`}>
              <Button scale="large" type="button" kind={theme.kind.primary}>
                Create a New Team
              </Button>
            </Link>
          </Box>
        </Flex>
      )}
    </>
  );
};

export default MyTeamTab;
